/* global snaptr */

import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { Link } from "react-router-dom";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getAnalytics, logEvent } from "firebase/analytics";
import { app } from "../firebase-config";
import { GooglePlayButton, AppStoreButton } from "react-mobile-app-button";
import { ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import styles from "./LeadCaptureForm.module.css";

const INTEREST_OPTIONS = [
  { value: "", label: "What brings you here?", description: "" },
  { value: "quit_myself", label: "🚭 I want to quit vaping", description: "Personal use" },
  { value: "reduce", label: "📉 I want to cut back", description: "Reduce usage gradually" },
  { value: "help_someone", label: "💙 Helping someone else", description: "Friend or family member" },
  { value: "healthcare", label: "🏥 Healthcare provider", description: "Clinical interest" },
  { value: "research", label: "🔬 Researcher", description: "Academic or clinical research" },
  { value: "employer", label: "🏢 Employer / HR", description: "Workplace wellness" },
  { value: "investor", label: "💰 Investor / Funding", description: "Investment opportunity inquiry" },
  { value: "curious", label: "👀 Just curious", description: "Learning more" },
];

const IOS_URL = "https://apps.apple.com/us/app/qwytt-nicotine/id6657960125";
const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.qwytt.nicotine.prod";

/**
 * Reusable lead capture form component
 * @param {Object} props
 * @param {string} props.pageName - Name of the page for analytics (e.g., "cold_lead", "homepage")
 * @param {string} props.landingPage - Landing page path for Firestore (e.g., "/learn")
 * @param {string} [props.title] - Form title (optional, has default)
 * @param {string} [props.subtitle] - Form subtitle (optional, has default)
 * @param {string} [props.buttonText] - Submit button text (optional, default: "Get Early Access")
 * @param {boolean} [props.showPerks] - Whether to show perks below form (default: true)
 * @param {string} [props.successVariant] - "download" shows app store links, "simple" shows just confirmation (default: "download")
 * @param {string} [props.successMessage] - Custom success message (optional, has defaults per variant)
 * @param {Function} [props.onSubmitSuccess] - Callback after successful submission
 * @param {string} [props.className] - Additional CSS class for container
 */
const LeadCaptureForm = ({
  pageName = "lead_form",
  landingPage = "/",
  title = "Ready to See Your Real Numbers?",
  subtitle = "Get early access to Qwytt and learn how data-driven nicotine reduction can work for you.",
  buttonText = "Get Early Access",
  showPerks = true,
  successVariant = "download",
  successMessage,
  onSubmitSuccess,
  className = "",
}) => {
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const dropdownRef = useRef(null);

  const analytics = getAnalytics(app);
  const selectedOption = INTEREST_OPTIONS.find((opt) => opt.value === interest);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getSnapClickId = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("sc_click_id") || undefined;
    } catch {
      return undefined;
    }
  };

  const handleInterestSelect = (value) => {
    setInterest(value);
    setIsDropdownOpen(false);
    logEvent(analytics, "interest_selected", { interest_type: value, page: pageName });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }
    if (!interest) {
      setError("Please select what brings you here");
      return;
    }

    setIsSubmitting(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const db = getFirestore(app);

      await addDoc(collection(db, "coldLeads"), {
        email: email.toLowerCase().trim(),
        interest,
        source: params.get("utm_source") || "direct",
        campaign: params.get("utm_campaign") || null,
        medium: params.get("utm_medium") || null,
        content: params.get("utm_content") || null,
        landingPage,
        createdAt: serverTimestamp(),
        unsubscribeToken: uuidv4(),
        unsubscribed: false,
        snapClickId: getSnapClickId() || null,
      });

      logEvent(analytics, "generate_lead", {
        currency: "USD",
        value: 1,
        page: pageName,
        interest_type: interest,
        source: params.get("utm_source") || "direct",
      });

      logEvent(analytics, "sign_up", {
        method: "email",
        page: pageName,
        interest_type: interest,
      });

      if (window.snaptr) {
        snaptr("track", "SIGN_UP", {
          sign_up_method: "email",
          uuid_c1: getSnapClickId(),
        });
      }

      setIsSubmitted(true);
      setEmail("");
      setInterest("");
      onSubmitSuccess?.();
    } catch (err) {
      console.error("Error saving lead:", err);
      setError("Something went wrong. Please try again.");
      logEvent(analytics, "lead_error", { error: err.message, page: pageName });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadClick = (platform) => {
    logEvent(analytics, "cta_click", {
      location: "success_state",
      action: "download_app",
      platform,
      page: pageName,
    });

    if (window.snaptr) {
      snaptr("track", "APP_OPEN", {
        destination: `app_store_${platform}`,
        uuid_c1: getSnapClickId(),
      });
    }
  };

  const handleLearnMoreClick = () => {
    logEvent(analytics, "cta_click", {
      location: "success_state",
      action: "learn_more",
      page: pageName,
    });
  };

  if (isSubmitted) {
    const defaultDownloadMessage = "Check your inbox for next steps. In the meantime, you can download the free app and start tracking today.";
    const defaultSimpleMessage = "Check your inbox for next steps. We'll keep you updated on launches, features, and exclusive offers.";
    const displayMessage = successMessage || (successVariant === "download" ? defaultDownloadMessage : defaultSimpleMessage);

    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.successState}>
          <div className={styles.successIcon}>
            <CheckCircle2 size={48} />
          </div>
          <h2 className={styles.successTitle}>You're In!</h2>
          <p className={styles.successText}>{displayMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${className}`}>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.subtitle}>{subtitle}</p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputWrapper}>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={styles.emailInput}
            disabled={isSubmitting}
          />
        </div>

        <div ref={dropdownRef} className={styles.dropdownContainer}>
          <button
            type="button"
            className={`${styles.dropdownTrigger} ${interest ? styles.hasValue : ""} ${isDropdownOpen ? styles.open : ""}`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            disabled={isSubmitting}
          >
            <span className={styles.dropdownLabel}>
              {selectedOption?.label || "What brings you here?"}
            </span>
            <ChevronDown
              size={20}
              className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.rotated : ""}`}
            />
          </button>

          {isDropdownOpen && (
            <ul className={styles.dropdownMenu} role="listbox">
              {INTEREST_OPTIONS.filter((opt) => opt.value !== "").map((option) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={interest === option.value}
                  className={`${styles.dropdownOption} ${interest === option.value ? styles.selected : ""}`}
                  onClick={() => handleInterestSelect(option.value)}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.description && (
                    <span className={styles.optionDescription}>{option.description}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
          {isSubmitting ? (
            <span className={styles.spinner}></span>
          ) : (
            <>
              <span>{buttonText}</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>

        {error && <p className={styles.errorText}>{error}</p>}
      </form>

      {showPerks && (
        <div className={styles.perks}>
          <div className={styles.perk}>
            <CheckCircle2 size={16} />
            <span>Free app access</span>
          </div>
          <div className={styles.perk}>
            <CheckCircle2 size={16} />
            <span>Sensor pre-order priority</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadCaptureForm;